import { useMemo, useState } from "react";
import CalculatorShell, { Field, Result, inputClass } from "@/components/CalculatorShell";
import { getCalculator } from "@/data/calculators";
import { noticeAssessment, noticeEndDate, NOTICE_SITUATIONS, gbp, longDate } from "@/lib/employment-calcs";

const calc = getCalculator("notice-period-calculator");

const weeks = (n) => `${n} week${n === 1 ? "" : "s"}`;

// What the law says about pay during notice, by rule (NOT-87 to NOT-91).
const PAY_RULES = {
  none: "Under one month's service there is no statutory minimum notice, so pay during notice follows the contract.",
  contract: "Pay during notice follows the contract: normal pay while working or on garden leave, and the PILON clause if paid in lieu.",
  s874: "The contract's notice is at least a week longer than the statutory minimum, so sections 88 to 91 do not apply (s87(4)). The employee gets what the contract and any statutory scheme (SSP, SMP and so on) provide, but there is no statutory top-up to full pay.",
  s88: "For the statutory notice period, the employee must get at least their normal pay for their normal hours while sick, injured, on holiday or on family leave (s88). Sick pay, SSP, SMP or holiday pay paid for that time counts towards it. Any notice beyond the statutory minimum follows the contract.",
  s89: "With no normal working hours, the employee must get at least a week's pay for each week of statutory notice, including while sick, on holiday or on family leave (s89). Sick pay, statutory pay or holiday pay paid for that time counts towards it.",
};

// NOT-END: quick end date, with no need for a start date.
function QuickEndDate() {
  const [q, setQ] = useState({ noticeDate: "", amount: "1", unit: "months" });
  const set = (k) => (e) => setQ((f) => ({ ...f, [k]: e.target.value }));
  const r = useMemo(() => (q.noticeDate ? noticeEndDate(q) : null), [q]);
  const notes = [];
  if (r && !r.error) {
    notes.push(`Notice runs from the day after it is given, so day one is ${longDate(r.firstDay)}, unless the contract or the notice letter sets a different start.`);
    if (r.monthEndAdjusted) notes.push("There is no matching date in the final month, so notice ends on the last day of that month.");
    notes.push("A contract can never give less than the statutory minimum. Use the full check below to compare the two.");
  }
  return (
    <div data-testid="notice-quick">
      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Date notice was given" hint="For a letter or email, the day the employee read it or had a reasonable chance to." htmlFor="q-notice">
          <input id="q-notice" type="date" className={inputClass} value={q.noticeDate} onChange={set("noticeDate")} data-testid="notice-quick-date" />
        </Field>
        <Field label="Length of notice" htmlFor="q-amount">
          <div className="flex gap-2">
            <input id="q-amount" type="number" min="1" step="1" inputMode="numeric" className={inputClass} value={q.amount} onChange={set("amount")} data-testid="notice-quick-amount" />
            <select aria-label="Unit" className={`${inputClass} max-w-[9rem]`} value={q.unit} onChange={set("unit")} data-testid="notice-quick-unit">
              <option value="days">days</option>
              <option value="weeks">weeks</option>
              <option value="months">months</option>
            </select>
          </div>
        </Field>
      </div>
      {r?.error && <p className="mt-6 text-sm font-semibold text-signal">{r.error}</p>}
      {r && !r.error && (
        <Result items={[{ label: "Last day of notice", value: longDate(r.lastDay), big: true, sub: `notice given ${longDate(r.noticeDate)}` }]} notes={notes} />
      )}
    </div>
  );
}

export default function NoticePeriodCalculator() {
  const [mode, setMode] = useState("quick");
  const [form, setForm] = useState({
    givenBy: "employer",
    startDate: "",
    noticeDate: "",
    contractAmount: "",
    contractUnit: "weeks",
    normalHours: "yes",
    situation: "working",
    weeklyPay: "",
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const ready = form.startDate && form.noticeDate;
  const r = useMemo(() => (ready ? noticeAssessment(form) : null), [form, ready]);

  const notes = [];
  if (r && !r.error) {
    if (r.contractShorter) notes.push(`The contract gives less than the statutory minimum, so the statutory ${weeks(r.statutoryWeeks)} applies instead (s86).`);
    notes.push(PAY_RULES[r.payRule]);
    if (r.givenBy === "employee" && ["s88", "s89"].includes(r.payRule)) {
      notes.push("Where the employee gave notice, this protection only applies if they leave at the end of it.");
    }
    if (r.situation === "pilon") {
      notes.push("Payment in lieu of notice is taxed and subject to National Insurance in full, and never counts towards the £30,000 tax-free amount.");
    }
    notes.push("Not covered here: time off the employee asks for during notice, leaving early by agreement, waiver of notice, and dismissal for gross misconduct (ss86(6) and 91).");
  }

  return (
    <CalculatorShell
      calc={calc}
      howItWorks={
        <>
          <p>Once an employee has worked for you for <strong>one month</strong>, the statutory minimum notice you must give is (Employment Rights Act 1996, s86(1)):</p>
          <ul className="list-disc space-y-1 pl-6">
            <li>one week, if they have less than two years' service</li>
            <li>one week for each complete year of service, from two years up to 12</li>
            <li>12 weeks, once they have 12 years' service or more</li>
          </ul>
          <p>
            <strong>When notice ends.</strong> Unless the contract or the notice letter says otherwise, notice starts to run
            on the day <em>after</em> it is given, so the day it is given does not count (<em>West v Kneels Ltd</em> [1987]
            ICR 146; <em>Wang v University of Keele</em> UKEAT/0223/10). A month's notice given on 3 November therefore ends on
            3 December. Where the final month has no matching date, notice ends on the last day of that month, so a month's
            notice given on 31 January ends on 28 February (<em>Dodds v Walker</em> [1981] 1 WLR 1027). Notice by letter or
            email is given when the employee reads it or has a reasonable chance to, not when it is sent or delivered
            (<em>Newcastle upon Tyne Hospitals NHS Foundation Trust v Haywood</em> [2018] UKSC 22).
          </p>
          <p>An employee only has to give <strong>one week</strong>, whatever their service (s86(2)). The contract can require more from either side, never less.</p>
          <p>
            <strong>Pay during notice</strong> depends on more than the length. If the employer's contractual notice is no
            more than six days longer than the statutory minimum, sections 88 to 91 protect pay during the statutory notice
            period even if the employee is sick, on holiday or on family leave. If it is at least a week longer, the contract
            alone decides (s87(4)).
          </p>
          <p>
            A week's pay is the normal weekly pay for normal hours, or a 12-week average where pay varies (ss221 to 224).
            Unlike redundancy pay, it is not capped.
          </p>
          <p>
            A <strong>payment in lieu of notice</strong> depends on the PILON clause, which may cover benefits, pension
            contributions or allowances as well as salary. The figure here is a basic-pay estimate, not the amount due under
            your clause. It is taxed as earnings.
          </p>
        </>
      }
      example={
        <p>
          Tom started on 10 January 2020 and you give him notice on 8 January 2026. He has five complete years, so the
          statutory minimum is five weeks. His contract says one month, which is shorter, so <strong>five weeks</strong>{" "}
          applies and his notice ends on 12 February. If he goes off sick during it, he is still entitled to his normal pay
          for those five weeks, less any sick pay he receives.
        </p>
      }
    >
      <Field label="What do you want to work out?" htmlFor="mode">
        <select id="mode" className={inputClass} value={mode} onChange={(e) => setMode(e.target.value)} data-testid="notice-mode">
          <option value="quick">When notice ends</option>
          <option value="full">Statutory minimum, end date and notice pay</option>
        </select>
      </Field>
      <div className="mt-6">
      {mode === "quick" ? <QuickEndDate /> : (
      <>
      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Who is giving notice?" htmlFor="given">
          <select id="given" className={inputClass} value={form.givenBy} onChange={set("givenBy")} data-testid="notice-given-by">
            <option value="employer">The employer (dismissal or redundancy)</option>
            <option value="employee">The employee (resignation)</option>
          </select>
        </Field>
        <Field label="Date continuous employment started" htmlFor="start">
          <input id="start" type="date" className={inputClass} value={form.startDate} onChange={set("startDate")} data-testid="notice-start" />
        </Field>
        <Field label="Date notice is given" htmlFor="notice">
          <input id="notice" type="date" className={inputClass} value={form.noticeDate} onChange={set("noticeDate")} data-testid="notice-date" />
        </Field>
        <Field label={`Notice the contract requires from the ${form.givenBy}`} hint="Optional. Leave blank if the contract says nothing." htmlFor="contract">
          <div className="flex gap-2">
            <input id="contract" type="number" min="0" step="1" inputMode="numeric" className={inputClass} value={form.contractAmount} onChange={set("contractAmount")} data-testid="notice-contract" />
            <select aria-label="Unit" className={`${inputClass} max-w-[9rem]`} value={form.contractUnit} onChange={set("contractUnit")} data-testid="notice-contract-unit">
              <option value="weeks">weeks</option>
              <option value="months">months</option>
            </select>
          </div>
        </Field>
        <Field label="Does the employee have normal working hours?" hint="Fixed contractual hours, even if pay varies." htmlFor="normal">
          <select id="normal" className={inputClass} value={form.normalHours} onChange={set("normalHours")} data-testid="notice-normal-hours">
            <option value="yes">Yes</option>
            <option value="no">No (for example zero hours)</option>
          </select>
        </Field>
        <Field label="During the notice period, the employee is…" htmlFor="situation">
          <select id="situation" className={inputClass} value={form.situation} onChange={set("situation")} data-testid="notice-situation">
            {NOTICE_SITUATIONS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </Field>
        <Field label="Weekly basic pay before tax (£)" hint="Optional, for a basic-pay estimate." htmlFor="pay">
          <input id="pay" type="number" min="0" step="0.01" inputMode="decimal" className={inputClass} value={form.weeklyPay} onChange={set("weeklyPay")} data-testid="notice-pay" />
        </Field>
      </div>

      {r?.error && <p className="mt-6 text-sm font-semibold text-signal">{r.error}</p>}
      {r && !r.error && (
        <Result
          items={[
            { label: "Notice ends", value: longDate(r.noticeEnd), big: true, sub: r.contractEnd && r.noticeEnd === r.contractEnd ? "contractual notice (longer than statutory)" : `statutory minimum of ${weeks(r.statutoryWeeks)}` },
            { label: "Statutory minimum", value: weeks(r.statutoryWeeks), sub: `${r.yearsOfService} complete year${r.yearsOfService === 1 ? "" : "s"} of service` },
            ...(r.basicEstimate != null ? [{ label: "Basic pay for the notice period", value: gbp(r.basicEstimate), sub: "estimate only: check the contract and any PILON clause" }] : []),
          ]}
          notes={notes}
        />
      )}
      </>
      )}
      </div>
    </CalculatorShell>
  );
}
